import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { concatMap, map, tap, toArray } from 'rxjs/operators'
import { of } from 'rxjs'
import { Toaster } from '../../services/toaster.service'

export interface RentalAssociatedCounts {
  bookings: number
  events: number
  images: number
  inquiries: number
  websites: number
}

@Injectable()
export class DeleteRentalService {
  constructor(private http: HttpClient, private toast: Toaster) {}

  getWebreadyRental(rentalId: string) {
    const url = `@webready/private/rentals/${rentalId}`
    return this.http.get(url)
  }

  getTokeetRentalCounts(rentalId: string) {
    const url = `@api/rental/subobjects/count/${rentalId}`
    return this.http.get<{ count: RentalAssociatedCounts }>(url).pipe(map(({ count }) => count))
  }

  getWebreadyRentalAssociated(rentalId: string, data?: { websites?: boolean; images?: boolean }) {
    const url = `@webready/private/rentals/associated_data`
    return this.http
      .post<{ rental: any; websites: any[]; images: { images: any[]; total: number } }>(url, { rentalId, ...data })
      .pipe(map((data) => ({ rental: data.rental, websites: data.websites, images: data.images?.images || [] })))
  }

  deleteWebreadyRental(rentalId: string, deleteImages = false) {
    const url = `@webready/private/rentals/delete`
    return this.http.post(url, { rentalId, deleteImages })
  }

  deleteHold(id: string) {
    const url = `@api/rental/delete/holds/${id}`

    return this.http.delete(url).pipe(tap(() => this.toast.success($localize`Hold events deleted successfully.`)))
  }

  deleteBookings(id: string, count = 500) {
    // The server needs about 0.3 s for each booking. 50 per call stays well below the 60 s proxy timeout.
    const limit = 50
    const batches = Math.ceil(count / limit)
    return of(...Array.from({ length: batches })).pipe(
      concatMap(() => {
        // Each call deletes the bookings it finds, so the next call starts again at skip=0.
        const url = `@api/rental/delete/inquiries/${id}?limit=${limit}&skip=0`
        return this.http.delete(url)
      }),
      toArray(),
      tap(() => this.toast.success($localize`Inquiries and bookings deleted successfully.`))
    )
  }

  deleteWebsites(id: string) {
    const url = `@api/rental/delete/websites/${id}`

    return this.http.delete(url).pipe(tap(() => this.toast.success($localize`Websites deleted successfully.`)))
  }

  delete(id: string) {
    const url = `@api/rental/delete/${id}`

    return this.http.delete(url).pipe(tap(() => this.toast.success($localize`Rental deleted successfully.`)))
  }
}
